import { createSignal, onCleanup } from "solid-js"
import type { AuthUser } from "@/transport/auth"

export type AuthStatus = "checking" | "authenticated" | "unauthenticated" | "unavailable"

export function createAuthSession(options: {
  check: (signal: AbortSignal) => Promise<AuthUser | null>
  /** The account to open as until the first check answers. */
  remembered?: AuthUser
}) {
  const [status, setStatus] = createSignal<AuthStatus>("checking")
  const [user, setUser] = createSignal<AuthUser | null>(options.remembered ?? null)
  let request: AbortController | undefined
  let pending: Promise<void> | undefined
  let timer: ReturnType<typeof setTimeout> | undefined
  let failures = 0
  let disposed = false

  const cancel = () => {
    clearTimeout(timer)
    request?.abort()
    request = undefined
    pending = undefined
  }
  const refresh = (): Promise<void> => {
    if (disposed) return Promise.resolve()
    if (pending) return pending
    clearTimeout(timer)
    const controller = new AbortController()
    request = controller
    if (!user()) setStatus("checking")
    pending = Promise.resolve().then(() => options.check(controller.signal)).then((value) => {
      if (controller.signal.aborted) return
      setUser(value)
      setStatus(value ? "authenticated" : "unauthenticated")
      failures = 0
    }).catch(() => {
      if (controller.signal.aborted) return
      setStatus("unavailable")
      // Retry session verification, never sign-in or a mutating request.
      const delay = Math.min(30_000, 1000 * 2 ** Math.min(failures++, 5))
      timer = setTimeout(() => { void refresh() }, delay)
    }).finally(() => {
      if (request !== controller) return
      pending = undefined
      request = undefined
    })
    return pending
  }
  const reset = () => {
    cancel()
    failures = 0
    setUser(null)
    setStatus("unauthenticated")
  }
  onCleanup(() => { disposed = true; cancel() })
  return { status, user, refresh, reset }
}
